export type PreparationPhase = 'application' | 'recording' | 'upload' | 'after';
export type PreparationTaskStatus = 'pending' | 'done' | 'not_applicable';
export type RepertoireDecision = 'confirm' | 'reuse' | 'new_take';
export type AuditionStage = 'planning' | 'submitted' | 'callback' | 'live_round' | 'closed';

export interface RepertoireEntry {
  id: string;
  composer: string;
  title: string;
  movement: string;
  durationMinutes?: number;
  decision: RepertoireDecision;
  note: string;
}

export interface RecordingSessionPlan {
  startPaddingSeconds?: number;
  endPaddingSeconds?: number;
  captureProfile?: string;
  mbPerMinute?: number;
  plannedAt: string;
  place: string;
  camera: string;
  audio: string;
  helperRole: string;
  roomNote: string;
}

export interface UploadHandoffPlan {
  bandwidthMbps: number;
  safetyBufferHours: number;
  filenameLabel: string;
  filenameAsciiOnly: boolean;
  portalNote: string;
}

export interface AuditionOutcome {
  stage: AuditionStage;
  recordedAt: string;
  nextAction: string;
  reflection: string;
}

export interface ProgramPreparation {
  programId: string;
  deadlineTimeZone: string;
  deadlineSourceNote: string;
  repertoire: RepertoireEntry[];
  session: RecordingSessionPlan;
  upload: UploadHandoffPlan;
  outcome: AuditionOutcome;
  taskStates: Record<string, PreparationTaskStatus>;
  updatedAt: string;
}

export interface PreparationTaskDefinition {
  id: string;
  phase: PreparationPhase;
  label: string;
  detail: string;
  officialRequired?: boolean;
}

export const preparationTasks: PreparationTaskDefinition[] = [
  { id: 'app-repertoire-wording', phase: 'application', label: '곡명·악장 표기를 요강 문구와 맞췄다', detail: '번역하거나 줄이지 말고 공식 표기를 다시 봐요.', officialRequired: true },
  { id: 'app-order', phase: 'application', label: '곡 순서와 분리 파일 여부를 확인했다', detail: '한 파일인지 곡별 파일인지 직접 표시해요.', officialRequired: true },
  { id: 'app-intro', phase: 'application', label: '영상 속 자기소개·수험번호 규칙을 확인했다', detail: '말하면 안 되는 블라인드 심사인지도 함께 봐요.', officialRequired: true },
  { id: 'app-accompaniment', phase: 'application', label: '반주·무반주·악보 사용 조건을 확인했다', detail: '반주자 동의와 일정은 별도로 잡아요.', officialRequired: true },
  { id: 'app-deadline-zone', phase: 'application', label: '마감 시각과 시간대를 원문에서 확인했다', detail: '날짜만 보고 자정으로 추측하지 않아요.', officialRequired: true },
  { id: 'app-rights', phase: 'application', label: '편집·후반작업·AI 고지 범위를 확인했다', detail: '허용된다고 적히지 않은 처리는 확인 필요로 남겨요.', officialRequired: true },
  { id: 'app-reuse', phase: 'application', label: '이전 영상 재사용 가능 여부를 확인했다', detail: '촬영 날짜·제출 곡·화면 규칙이 모두 같을 때만 다시 쓸 수 있어요.' },
  { id: 'record-storage', phase: 'recording', label: '촬영 기기 저장공간과 전원을 확보했다', detail: '긴 연주 영상 두 개 이상을 남길 여유를 확인해요.' },
  { id: 'record-airplane', phase: 'recording', label: '방해금지·비행기 모드와 자동 잠금을 점검했다', detail: '전화·알림·화면 잠금으로 촬영이 끊기지 않게 해요.' },
  { id: 'record-frame', phase: 'recording', label: '악기·손·얼굴·전신의 필수 구도를 확인했다', detail: '기관이 요구한 범위를 실제 화면으로 대조해요.', officialRequired: true },
  { id: 'record-orientation', phase: 'recording', label: '가로·세로와 해상도 규칙을 확인했다', detail: '기본값이 아니라 해당 지원의 원문을 따라요.', officialRequired: true },
  { id: 'record-audio', phase: 'recording', label: '가장 큰 소리에서 찢어짐이 없는지 시험했다', detail: '짧은 테스트를 녹화해 기기 스피커와 이어폰으로 들어요.' },
  { id: 'record-noise', phase: 'recording', label: '냉난방·교통·문·보면대 잡음을 확인했다', detail: '연주 전에 방을 20초 녹화해 돌발 소리를 찾아요.' },
  { id: 'record-instrument', phase: 'recording', label: '조율·현·리드·페달·의자 상태를 확인했다', detail: '연주 직전 바뀔 수 있는 물리 상태를 마지막에 봐요.' },
  { id: 'record-slate', phase: 'recording', label: '시작·끝 여백과 인사 방식을 정했다', detail: '촬영자와 연주자가 같은 종료 신호를 공유해요.' },
  { id: 'record-playback', phase: 'recording', label: '각 연주 영상을 끝까지 따로 재생했다', detail: '메모리카드에 있다는 사실과 재생 가능은 달라요.' },
  { id: 'record-backup', phase: 'recording', label: '선택 전 원본 영상을 다른 저장장치에도 보관했다', detail: '보낼연주는 영구 원본 보관함이 아니에요.' },
  { id: 'upload-name', phase: 'upload', label: '파일명이 지원처 규칙과 일치한다', detail: '공백·한글·특수문자 제한이 있으면 공식 규칙을 우선해요.', officialRequired: true },
  { id: 'upload-size', phase: 'upload', label: '보낼 파일 크기와 제한을 다시 비교했다', detail: '보낼 영상으로 정한 파일의 실제 크기를 기준으로 봐요.' },
  { id: 'upload-buffer', phase: 'upload', label: '업로드·처리·재시도 시간을 마감 전에 확보했다', detail: '예상 시간에 지원처의 처리 시간을 따로 더해요.' },
  { id: 'upload-preview', phase: 'upload', label: '지원처 미리보기에서 처음부터 끝까지 재생했다', detail: '이 기기에서 재생한 것만으로 제출 완료로 보지 않아요.', officialRequired: true },
  { id: 'upload-fields', phase: 'upload', label: '곡명·작곡가·악장 입력을 파일과 대조했다', detail: '복사한 텍스트가 잘리거나 순서가 바뀌지 않았는지 봐요.' },
  { id: 'upload-payment', phase: 'upload', label: '지원료·면제·결제 완료 상태를 지원처에서 확인했다', detail: '이 서비스는 결제 상태를 읽거나 대신 결제하지 않아요.', officialRequired: true },
  { id: 'upload-confirmation', phase: 'upload', label: '확인번호·완료 화면·이메일을 기록했다', detail: '사용자 기록과 기관의 실제 접수 상태는 구분해요.', officialRequired: true },
  { id: 'upload-backup', phase: 'upload', label: '최종 원본·파일 확인 코드·제출 완료 화면을 따로 보관했다', detail: '제출 기록 백업에는 원본 영상이 포함되지 않아요.' },
  { id: 'after-notification', phase: 'after', label: '결과 알림 방법과 예상 날짜를 적었다', detail: '이메일·지원처 홈페이지·전화 중 실제 안내된 방법을 확인해요.' },
  { id: 'after-live-deadline', phase: 'after', label: '다음 라운드 일정과 응답 기한을 확인했다', detail: '합격 여부를 자동 추정하지 않고 공식 알림만 기록해요.' },
  { id: 'after-materials', phase: 'after', label: '악보·반주자·신분증·현장 요구물을 정리했다', detail: '영상 제출과 현장 오디션의 준비물을 분리해요.' },
  { id: 'after-travel', phase: 'after', label: '이동·숙박·악기 운송의 취소 가능 조건을 확인했다', detail: '공식 일정 전에는 비용 확정을 서두르지 않아요.' },
  { id: 'after-reflection', phase: 'after', label: '다음 촬영에 유지할 점과 바꿀 점을 남겼다', detail: '합격 예측이나 예술성 점수 대신 내가 확인한 사실을 적어요.' },
  { id: 'after-reuse', phase: 'after', label: '최종 영상을 재사용·재촬영·보류 중 하나로 분류했다', detail: '다음 지원처의 요강 내용이 다르면 다시 확인해요.' },
];

const emptySession = (): RecordingSessionPlan => ({
  plannedAt: '', place: '', camera: '', audio: '', helperRole: '', roomNote: '',
});

export const DEFAULT_UPLOAD_MBPS = 20;

const emptyUpload = (): UploadHandoffPlan => ({
  bandwidthMbps: DEFAULT_UPLOAD_MBPS, safetyBufferHours: 6, filenameLabel: '', filenameAsciiOnly: false, portalNote: '',
});

const emptyOutcome = (): AuditionOutcome => ({
  stage: 'planning', recordedAt: '', nextAction: '', reflection: '',
});

export function createProgramPreparation(programId: string): ProgramPreparation {
  return {
    programId,
    deadlineTimeZone: '',
    deadlineSourceNote: '',
    repertoire: [],
    session: emptySession(),
    upload: emptyUpload(),
    outcome: emptyOutcome(),
    taskStates: {},
    updatedAt: new Date().toISOString(),
  };
}

export function normalizeProgramPreparation(programId: string, value?: Partial<ProgramPreparation>): ProgramPreparation {
  const base = createProgramPreparation(programId);
  return {
    ...base,
    ...value,
    programId,
    repertoire: Array.isArray(value?.repertoire) ? value.repertoire : [],
    session: { ...base.session, ...value?.session },
    upload: { ...base.upload, ...value?.upload },
    outcome: { ...base.outcome, ...value?.outcome },
    taskStates: value?.taskStates && typeof value.taskStates === 'object' ? value.taskStates : {},
  };
}

export function preparationProgress(plan: ProgramPreparation, phase?: PreparationPhase) {
  const tasks = phase ? preparationTasks.filter((task) => task.phase === phase) : preparationTasks;
  const resolved = tasks.filter((task) => ['done', 'not_applicable'].includes(plan.taskStates[task.id] ?? 'pending')).length;
  return { resolved, total: tasks.length, percent: tasks.length ? Math.round((resolved / tasks.length) * 100) : 0 };
}

export function estimateUploadMinutes(bytes: number, bandwidthMbps: number): number | null {
  if (!Number.isFinite(bytes) || bytes <= 0 || !Number.isFinite(bandwidthMbps) || bandwidthMbps <= 0) return null;
  const seconds = (bytes * 8) / (bandwidthMbps * 1_000_000);
  return Math.max(1, Math.ceil((seconds * 1.35) / 60));
}

const safeToken = (value: string, asciiOnly = false): string => value
  .normalize('NFC')
  .replace(asciiOnly ? /[^a-zA-Z0-9]+/g : /[^a-zA-Z0-9가-힣]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 36);

export function suggestedFileName(school: string, label: string, extension = 'mp4', asciiOnly = false): string {
  const parts = [safeToken(school, asciiOnly), safeToken(label || 'final', asciiOnly)].filter(Boolean);
  return `${parts.join('_') || 'audition-final'}.${safeToken(extension, true) || 'mp4'}`;
}

export function latestSafeUploadStart(deadline: string, uploadMinutes: number | null, safetyBufferHours: number): string | null {
  const time = new Date(deadline).getTime();
  if (!Number.isFinite(time)) return null;
  const totalMinutes = Math.max(0, uploadMinutes ?? 0) + Math.max(0, safetyBufferHours) * 60;
  return new Date(time - totalMinutes * 60_000).toISOString();
}

export function preparationHandoff(plan: ProgramPreparation, take?: { fileName: string; bytes: number; hash: string }) {
  return {
    schema: 'audition-preparation-handoff-v1',
    exportedAt: new Date().toISOString(),
    plan,
    lockedTake: take ?? null,
    rawMediaIncluded: false,
    providerSubmissionConfirmed: false,
  };
}
